import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Col, Divider, Row } from 'antd';
import { cloneDeep, maxBy } from 'es-toolkit';

import { routeFileSelector } from '@/utilz';
import { getLocalDateTime } from '@/utils/date';
import EmptyRecords from '@/views/components/Common/EmptyRecords';
import ScrollPagination from '@/views/components/Paginations/ScrollPagination';
import ContentSpinner from '@/views/components/Spinners/ContentSpinner';

import { LADCovertDatesFromMomentToAPIType } from '../../../../../../utilz/PreClaimsUtils';
import Button from '../../../../../components/Buttons/Button';
import PrimaryLabel from '../../../../../components/Labels/PrimaryLabel';
import PreClaimsNotesCard from '../../../PreClaims/components/CreateTabs/Tabs/Notes/PreClaimsNotesCard';
import { useGetClaimDetails, useGetClaimNotes, useSaveClaim } from '../../hooks';
import { getTabDisplayState } from '../utils/getTabDisplayState';

const LADClaimsNotesTab = ({ claimKey }) => {
  const [newNotes, setNewNotes] = useState([]);
  const [noteEditModes, setNoteEditModes] = useState({});
  const [noteOverrides, setNoteOverrides] = useState({});
  const nextLocalNoteKeyRef = useRef(1);
  const { data: claimDetails } = useGetClaimDetails(claimKey);
  const {
    data: claimNotesData,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
  } = useGetClaimNotes({ claimKey });
  const { mutate: saveClaim } = useSaveClaim();
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const user = useSelector((state) => state.auth.user?.data);

  const claimNotes = useMemo(() => {
    if (!claimNotesData) {
      return { data: [], totalRows: 0 };
    }
    const legacyNotes = claimNotesData.pages?.flatMap((page) => page.data ?? []) ?? [];
    return {
      data: claimNotesData.notes ?? legacyNotes,
      totalRows: claimNotesData.totalRows || claimNotesData.pages?.[0]?.totalRows || 0,
    };
  }, [claimNotesData]);
  const notesList = useMemo(() => {
    const uniqueExistingNotes = Array.from(
      new Map((claimNotes.data || []).map((row) => [row.sequenceNo, row])).values(),
    );
    const existing = uniqueExistingNotes.map((row) => {
      const key = row.sequenceNo;
      return {
        ...row,
        ...(noteOverrides[key] ?? {}),
        editMode: noteEditModes[key] ?? row.editMode ?? false,
        isNew: false,
      };
    });
    return [...newNotes, ...existing];
  }, [claimNotes.data, newNotes, noteEditModes, noteOverrides]);
  const { showContent, showEmpty, showFullLoader, showNextPageLoader } = useMemo(
    () =>
      getTabDisplayState({
        hasRows: notesList.length > 0,
        isFetching,
        isFetchingNextPage,
        isLoading,
      }),
    [isFetching, isFetchingNextPage, isLoading, notesList.length],
  );

  const getNoteKey = (note) => (note?.isNew && note?.localNoteKey ? note.localNoteKey : note?.sequenceNo);

  const handleSubmit = (data, index) => {
    const currentNote = notesList[index];
    if (!currentNote) {
      return;
    }
    const updatedNote = {
      ...data,
      createdByUser: `${user?.firstName} ${user?.lastName}`,
      createdOn: getLocalDateTime().toISOString(),
      editMode: false,
      isNew: currentNote.isNew,
      localNoteKey: currentNote.localNoteKey,
      sequenceNo: currentNote.sequenceNo,
    };
    if (currentNote.isNew) {
      setNewNotes((prev) => prev.map((note) => (getNoteKey(note) === getNoteKey(currentNote) ? updatedNote : note)));
    } else {
      setNoteOverrides((prev) => ({ ...prev, [getNoteKey(currentNote)]: updatedNote }));
      setNoteEditModes((prev) => ({ ...prev, [getNoteKey(currentNote)]: false }));
    }
    if (!claimDetails) {
      return;
    }
    let payload = cloneDeep(claimDetails);
    const noteDetails = {
      ...data,
      createdByUser: `${user?.firstName} ${user?.lastName}`,
      createdOn: getLocalDateTime().toISOString(),
    };
    const dirtyFlag = currentNote.isNew ? 'I' : 'U';

    payload = LADCovertDatesFromMomentToAPIType(payload);
    payload.claimComment = [{ ...noteDetails, dirtyFlag }];
    payload.section = 'COMMENTS';
    payload.pageIndex = claimNotesData?.pageIndex ?? 1;
    saveClaim(payload, {
      onSuccess: () => {
        if (currentNote.isNew) {
          setNewNotes((prev) => prev.filter((note) => getNoteKey(note) !== getNoteKey(currentNote)));
        }
      },
    });
  };

  const setEditMode = (index, val) => {
    const note = notesList[index];
    if (!note) {
      return;
    }
    if (note.isNew) {
      setNewNotes((prev) =>
        prev.map((row) => (getNoteKey(row) === getNoteKey(note) ? { ...row, editMode: val } : row)),
      );
      return;
    }
    setNoteEditModes((prev) => ({ ...prev, [getNoteKey(note)]: val }));
  };

  const handleDelete = (index, isNew) => {
    if (isNew) {
      const note = notesList[index];
      if (!note) {
        return;
      }
      setNewNotes((prev) => prev.filter((row) => getNoteKey(row) !== getNoteKey(note)));
    } else {
      if (!claimDetails) {
        return;
      }
      let payload = cloneDeep(claimDetails);
      const noteToDelete = notesList[index];
      if (!noteToDelete) {
        return;
      }
      setNoteOverrides((prev) => {
        const next = { ...prev };
        delete next[getNoteKey(noteToDelete)];
        return next;
      });
      setNoteEditModes((prev) => ({ ...prev, [getNoteKey(noteToDelete)]: false }));

      payload = LADCovertDatesFromMomentToAPIType(payload);
      payload.claimComment = [{ ...noteToDelete, dirtyFlag: 'D' }];
      payload.section = 'COMMENTS';
      payload.pageIndex = claimNotesData?.pageIndex ?? 1;
      saveClaim(payload);
    }
  };

  const handleCancel = (index, isNew) => {
    const currentNote = notesList[index];
    if (!currentNote) {
      return;
    }
    if (isNew) {
      setNewNotes((prev) => prev.filter((row) => getNoteKey(row) !== getNoteKey(currentNote)));
    } else {
      const originalNote = (claimNotes.data || []).find((n) => getNoteKey(n) === getNoteKey(currentNote));
      if (originalNote) {
        setNoteOverrides((prev) => {
          const next = { ...prev };
          delete next[getNoteKey(originalNote)];
          return next;
        });
        setNoteEditModes((prev) => ({ ...prev, [getNoteKey(originalNote)]: false }));
      } else {
        setNoteEditModes((prev) => ({ ...prev, [getNoteKey(currentNote)]: false }));
      }
    }
  };

  const handleAddNew = () => {
    let nextSequence = maxBy([...claimNotes.data, ...newNotes], (note) => Number(note.sequenceNo));
    nextSequence = nextSequence ? Number(nextSequence.sequenceNo) || 0 : 0;
    setNewNotes((prev) => [
      {
        claimKey: claimDetails?.claimKey,
        commentText: undefined,
        commentType: undefined,
        dirtyFlag: 'i',
        editMode: true,
        isNew: true,
        localNoteKey: `new-note-${nextLocalNoteKeyRef.current++}`,
        oldCommentType: null,
        sequenceNo: nextSequence + 1,
      },
      ...prev,
    ]);
  };

  const handleNextPage = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return (
    <div className="h-100 d-flex flex-direction-column">
      <div className="py-10 px-30">
        <Row justify="space-between" align="middle">
          <Col>
            <PrimaryLabel>Notes</PrimaryLabel>
          </Col>
          <Col>
            <Button variant="success" dataTestId="add-new-button" onClick={handleAddNew}>
              Add New
            </Button>
          </Col>
        </Row>
      </div>
      <div>
        <Divider className="m-0 border-bottom-border" />
      </div>
      {showFullLoader ? (
        <div className="px-30 py-10 flex-1" data-testid="lad-notes-loading">
          <ContentSpinner />
        </div>
      ) : showContent ? (
        <ScrollPagination
          className="px-30 py-10 flex-1"
          loadMore={handleNextPage}
          isLoading={showNextPageLoader}
          hasMore={isFetchingNextPage ? false : (hasNextPage ?? false)}
          scrollOffset={30}
        >
          {notesList.map((row, index) => (
            <PreClaimsNotesCard
              noteDetails={row}
              key={getNoteKey(row)}
              index={index}
              editMode={Boolean(row.editMode)}
              setEditMode={setEditMode}
              handleCancel={handleCancel}
              handleSubmit={handleSubmit}
              handleDelete={handleDelete}
            />
          ))}
        </ScrollPagination>
      ) : showEmpty ? (
        <EmptyRecords
          title={t('translation:default.documentManager.noFoundText')}
          descriptionOne={t('translation:claims.emptyClaimNotes')}
          descriptionTwo={''}
        />
      ) : null}
    </div>
  );
};

export default LADClaimsNotesTab;
